import React from 'react';
import { SocialMediaPost, SOCIAL_POST_STATUS_DRAFT, SocialMediaContent } from '../../types';
import {
  FacebookPageDto,
  InstagramAccountDto,
  SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE,
  SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT,
} from '../../../social-posting/socialPostingApi';
import { useFetchedFacebookAccount } from '../../../facebook/hooks/useFetchedFacebookAccount';
import { useConnectFacebook } from '../../../facebook/hooks/useConnectFacebook';
import { SocialMediaAccountPost } from './SocialMediaAccountPost';
import { useMemoCompare } from '../../../hooks/useMemoCompare';
import _ from 'lodash';
import { Button } from 'react-bootstrap';

export const SocialMediaAccountList = (props: {
  posts: SocialMediaPost[];
  content: SocialMediaContent;
  publishOn: Date;
  onChanged: (posts: SocialMediaPost[]) => void;
}) => {
  const { posts, content, publishOn, onChanged } = props;

  const {
    query: { data: facebookAccount, isLoading, isError },
    queryStatusComponent,
  } = useFetchedFacebookAccount();

  const { startConnect, isConnecting } = useConnectFacebook();

  const isConnected = facebookAccount && !facebookAccount.isDisconnected;
  const isNotConnected = !facebookAccount || facebookAccount.isDisconnected;

  const handleConnectClick = () => {
    startConnect();
  };

  const getMatchedAccounts = () => {
    if (!isConnected || !facebookAccount) {
      return [];
    }

    const matchedAccounts: Array<{
      post: SocialMediaPost | null;
      account: FacebookPageDto | InstagramAccountDto | null;
    }> = [];

    facebookAccount.facebookPages.forEach((page) => {
      const post = posts.find(
        (post) => post.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE && post.socialAssetId === page.id
      );
      matchedAccounts.push({
        post: post ? post : null,
        account: page,
      });
    });

    facebookAccount.instagramAccounts.forEach((account) => {
      const post = posts.find(
        (post) =>
          post.type === SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT && post.socialAssetId === account.id
      );
      matchedAccounts.push({
        post: post ? post : null,
        account: account,
      });
    });

    posts.forEach((post) => {
      const pageFound =
        post.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE
          ? facebookAccount.facebookPages.some((page) => page.id === post.socialAssetId)
          : facebookAccount.instagramAccounts.some((page) => page.id === post.socialAssetId);
      if (!pageFound) {
        matchedAccounts.push({
          post,
          account: null,
        });
      }
    });

    return matchedAccounts;
  };

  const matchedAccounts = useMemoCompare(getMatchedAccounts(), (a, b) => _.isEqual(a, b));

  const handleSelectionChange = (
    isSelected: boolean,
    account: FacebookPageDto | InstagramAccountDto
  ) => {
    const newPosts = [...posts];

    if (isSelected) {
      const newPost: SocialMediaPost = {
        socialAssetId: account.id,
        status: SOCIAL_POST_STATUS_DRAFT,
        publishing: null,
        type: account.type,
      };
      newPosts.push(newPost);
    } else {
      const index = newPosts.findIndex(
        (post) => post.type === account.type && post.socialAssetId === account.id
      );
      if (index !== -1) {
        newPosts.splice(index, 1);
      }
    }

    onChanged(newPosts);
  };

  if (isLoading) {
    return <div>Loading social media accounts...</div>;
  }

  if (isError) {
    return queryStatusComponent;
  }

  if (isNotConnected) {
    return (
      <div className="social-media-account-list">
        <Button
          variant="primary"
          className="button-apply"
          onClick={handleConnectClick}
          disabled={isConnecting}
        >
          {isConnecting ? 'Connecting...' : 'Connect Facebook & Instagram'}
        </Button>
      </div>
    );
  }

  return (
    <div className="social-media-account-list">
      <div className="social-media-accounts-horizontal">
        {matchedAccounts.map(({ post, account }, index) => (
          <SocialMediaAccountPost
            key={`${account?.type ? account.type : post?.type}-${account?.id}-${index}`}
            post={post}
            account={account}
            content={content}
            publishOn={publishOn}
            readonly={false}
            onSelectionChange={(isSelected) => {
              if (account) handleSelectionChange(isSelected, account);
            }}
          />
        ))}
      </div>
    </div>
  );
};
